import Subtask from "./Subtask";
import BaseController from "../../../controller/BaseController";

import TreeTable, { TreeTable$ToggleOpenStateEvent } from "sap/ui/table/TreeTable";
import AutoRowMode from "sap/ui/table/rowmodes/Auto";
import Column from "sap/ui/table/Column";

import Toolbar from "sap/m/Toolbar";
import ToolbarSpacer from "sap/m/ToolbarSpacer";
import Button from "sap/m/Button";
import Link from "sap/m/Link";
import Text from "sap/m/Text";
import HBox from "sap/m/HBox";
import Label from "sap/m/Label";

import Icon from "sap/ui/core/Icon";
import CustomData from "sap/ui/core/CustomData";

import JSONModel from "sap/ui/model/json/JSONModel";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import Model from "sap/ui/model/Model";

// JS utilities already present in the project
// Keep imports to JS modules minimal and typed as any to avoid TS typing frictions.
import PHPBackendRequest from "../handlers/PHPBackendRequest";
import ExportUtils from "./utils/ExportUtils";
import VBox from "sap/m/VBox";
import Context from "sap/ui/model/Context";
import Component from "../../../Component";
import Control from "sap/ui/core/Control";
import DialogUtils from "./utils/DialogUtils";

type TreeCreationParams = {
	// Backend source for data
	module: string;
	task: string;
};

type TreeviewResponse = {
	treeview?: {
		title: string;
		group?: string;
		level?: [unknown, string];
		nav: {
			module: string;
			task: string;
			field: string;
		};
		columns: Record<
			string,
			{
				name: string;
				type: string;
				width: string;
				pos?: number;
			}
		>;
		data: Record<string, unknown>[];
	};
};

type ComplexTypeConfig = {
	table: string;
	listBinding?: ODataListBinding;
	filters?: Filter[];
	objects?: { id: string; name: string }[];
	objectMap?: Map<string, { id: string; name: string }>;
};

export default class _Tree extends Subtask {
	private params: TreeCreationParams;

	private treeTable?: TreeTable;
	private toolbar?: Toolbar;

	private columns!: TreeviewResponse["treeview"]["columns"];
	private rows: Record<string, unknown>[] = [];
	private nav!: TreeviewResponse["treeview"]["nav"];
	private groupColumn?: string;
	private title?: string;
	private level?: [unknown, string];

	private static COMPLEX_TYPES: Record<string, ComplexTypeConfig> = {
		client: { table: "crm_clients_bs" },
		contact: { table: "crm_contacts_bs" },
		shop: { table: "crm_shops_bs" },
		alert: { table: "cfr_alert_bs" },
		roster: { table: "cfr_rosters_bs" },
	};

	constructor(controller: BaseController, subtaskId: string, params: TreeCreationParams) {
		super(controller, subtaskId);
		this.metadata = { name: "Tree", subtaskId };
		this.params = params;

		// Build UI and data pipeline
		this.init().catch((e) => console.error("Tree init failed:", e));
	}

	private async init(): Promise<void> {
		// Container is the subtask root (e.g., VBox/Panel) with this.subtaskId
		const container = this.getContainer();
		this.toolbar = this.buildToolbar();
		container.addItem(this.toolbar);

		this.treeTable = this.createTreeTable();
		container.addItem(this.treeTable);

		this.treeTable.setBusy(true);

		const data = await this.fetchData();
		if (!data?.treeview) {
			this.treeTable.setBusy(false);
			return;
		}

		// Extract metadata
		this.columns = data.treeview.columns;
		this.rows = data.treeview.data || [];
		this.groupColumn = data.treeview.group;
		this.nav = data.treeview.nav;
		this.title = data.treeview.title;
		this.level = data.treeview.level;

		// Columns
		this.buildColumns();

		// Explode complex type IDs into objects
		const model = this.controller.getOwnerComponent().getModel();
		await this.explodeRows(this.rows, this.columns, model);

		// Build hierarchy and bind
		const rootNodes = this.buildHierarchy(this.rows, this.groupColumn);
		this.bindRows(rootNodes);

		this.attachRowExpansionSync(this.treeTable);

		this.treeTable.setBusy(false);
	}

	// ---------- UI construction ----------

	private buildToolbar(): Toolbar {
		const bar = new Toolbar();
		bar.addContent(new ToolbarSpacer());

		const exportBtn = new Button({
			icon: "sap-icon://fa-light/file-excel",
			press: this.onExport.bind(this),
			tooltip: "Export",
		});
		const copyBtn = new Button({
			icon: "sap-icon://fa-light/copy",
			press: this.onCopy.bind(this),
			tooltip: "Copy",
		});

		bar.addContent(exportBtn);
		bar.addContent(copyBtn);
		return bar;
	}

	private createTreeTable(): TreeTable {
		const table = new TreeTable({
			rowMode: new AutoRowMode({
				minRowCount: "{= Math.min(${rowModel>/currentRowCount}, 10000)}",
				rowContentHeight: 48,
			}), // rowmodes typings can be finicky, keep as any
			busy: true,
			selectionMode: "None",
		});
		table.addStyleClass("treeview-table");
		return table;
	}

	private attachRowExpansionSync(table: TreeTable): void {
		table.attachToggleOpenState((event: TreeTable$ToggleOpenStateEvent) => {
			const rowModel = table.getModel("rowModel") as JSONModel;
			if (!rowModel) return;

			let count = rowModel.getProperty("/currentRowCount") as number || 0;
			const params = event.getParameters();
			const rowCtx = params.rowContext as Context;
			const rowObj = rowCtx.getObject() as { children?: unknown[] };
            if (!rowObj || !rowObj.children) return;
            const children = rowObj.children;
			const delta = params?.expanded ? children.length : -children.length;

			count += delta;
			rowModel.setProperty("/currentRowCount", count);
			(table.getRowMode() as AutoRowMode).setMinRowCount(count);
		});
	}

	// ---------- Data fetching ----------

	private async fetchData(): Promise<TreeviewResponse> {
		// Retrieve user from the OwnerComponent (adjust if your app exposes it differently)
		const component: Component = this.controller.getOwnerComponent();
		const user = component.getUser();
		const request = new PHPBackendRequest(
			this.params.module,
			this.params.task,
			user,
		);

		// Support different HTTPRequest APIs (send/execute) and response shapes
        let json: TreeviewResponse;
        try {
            const response = await request.send();
            json = await response.json() as TreeviewResponse;
        } catch (e) {

            throw new Error(`Data fetch failed: ${(e as Error).message}`);
        }
		return json;
	}

	// ---------- Columns ----------

	private buildColumns(): void {
		if (!this.treeTable) return;
		const colNames = Object.keys(this.columns || {});
		colNames.forEach((name, i) => (this.columns[name].pos = i));

		colNames.forEach((colName, i) => {
			const meta = this.columns[colName];
			const template = this.getTemplateForColumn(colName, meta.type);

			const col = new Column({
				filterProperty: meta.type === "date" ? undefined : colName,
				sortProperty: colName,
				minWidth: meta.width === "" ? 0 : parseInt(meta.width, 10),
				label: new Label({ text: meta.name }),
				template,
			});

			// Disable sorting/filtering for complex types, use <name>_concat for filtering
			if (Tree.isComplexType(meta.type)) {
				col.setSortProperty(undefined);
				col.setShowSortMenuEntry(false);
				col.setFilterProperty(colName + "_concat");
			}

			// Special handling for first column -> use link and navigation
			if (i === 0) {
				const link = new Link({
					text: `{rowModel>${colName}}`,
					tooltip: `{rowModel>${colName}}`,
					press: this.controller.onTaskNav.bind(this.controller),
					enabled: "{= !%{rowModel>isGroupHeader}}",
					subtle: "{= %{rowModel>isGroupHeader}}",
					customData: [
						new CustomData({ key: "module", value: this.nav.module }),
						new CustomData({ key: "task", value: this.nav.task }),
						new CustomData({ key: "field", value: this.nav.field }),
						new CustomData({ key: "value", value: `{rowModel>${this.nav.field}}` }),
					],
				});
				col.setTemplate(link);
			}

			this.treeTable.addColumn(col);
		});
	}

	private getTemplateForColumn(colName: string, type: string): Control {
		// Special 'valid' icon column
		if (colName === "valid") {
			const icon = new Icon({
				src: "{= %{path: 'rowModel>valid', targetType: 'string'} === 'true' ? 'sap-icon://fa-light/check' : (%{path: 'rowModel>valid_color', targetType: 'string'} === 'red' ? 'sap-icon://fa-solid/triangle-exclamation' : 'sap-icon://fa-light/triangle-exclamation')}",
				color: "{rowModel>valid_color}",
				tooltip: "{rowModel>valid_message}",
				press: this.controller.onTaskNav.bind(this.controller),
				visible: "{= !%{path: 'rowModel>isGroupHeader', targetType: 'boolean'}}",
			});
			icon.addCustomData(new CustomData({ key: "module", value: this.nav.module }));
			icon.addCustomData(new CustomData({ key: "task", value: this.nav.module + "_show" }));
			icon.addCustomData(new CustomData({ key: "field", value: "id" }));
			icon.addCustomData(new CustomData({ key: "value", value: `{rowModel>id}` }));
			return icon;
		}

		switch (type) {
			case "date":
				return new Text({
					text: {
						path: `rowModel>${colName}`,
						type: new (this.controller.formats.Date)(),
					},
				});
			case "number":
				return new Text({
					text: {
						path: `rowModel>${colName}`,
						type: new (this.controller.formats.Number)(),
					},
				});
			case "checkbox":
				return new Text({
					text: { path: `rowModel>${colName}` }
				});
			default:
				if (Tree.isComplexType(type)) {
					const hbox = new HBox({ wrap: "Wrap"});
					hbox.addStyleClass("treeview-cell-hbox");
					hbox.addStyleClass("sapUiTinyMarginEnd");
					const link = new Link({
						text: "{rowModel>name}",
						tooltip: "{rowModel>name}",
                        press: () => {
                            DialogUtils.openDialog(this.controller, `${type}_profile`).catch((e) => {
                                console.error(`Failed to open ${type} dialog:`, e);
                            });
                        },
						customData: [
							new CustomData({ key: "module", value: type + "s" }),
							new CustomData({ key: "dialogName", value: "overview" }),
							new CustomData({ key: "tableName", value: Tree.COMPLEX_TYPES[type].table }),
							new CustomData({ key: "field", value: "id" }),
							new CustomData({ key: "value", value: "{rowModel>id}" }),
						],
					});
					link.addStyleClass("sapUiTinyMarginEnd");
					hbox.bindAggregation("items", {
						path: `rowModel>${colName}`,
						templateShareable: false,
						template: link,
					});
					return hbox;
				}
				return new Text({ text: `{rowModel>${colName}}` });
		}
	}

	// ---------- Complex type explosion ----------

	private static isComplexType(type: string): boolean {
		return Object.prototype.hasOwnProperty.call(this.COMPLEX_TYPES, type);
	}

	private async explodeRows(
		aRows: Record<string, unknown>[],
		mColumns: Record<string, { type: string }>,
		oModel: Model
	): Promise<void> {
		this.collectFiltersForComplexTypes(aRows, mColumns, oModel);
		await this.fetchObjectsForComplexTypes();
		this.replaceIdsWithObjects(aRows, mColumns);
	}

	private collectFiltersForComplexTypes(
		aRows: Record<string, unknown>[],
		mColumns: Record<string, { type: string }>,
		oModel: Model
	): void {
		aRows.forEach((row) => {
			for (const key in row) {
				if (!mColumns[key]) continue;
				const type = mColumns[key].type;
				if (!Tree.isComplexType(type)) continue;

				const conf = Tree.COMPLEX_TYPES[type];
				if (!conf.listBinding) {
					conf.listBinding = oModel.bindList(`/${conf.table}`, undefined, undefined, undefined, {
						$select: "id,name",
						$filter: "status lt 90",
					}) as ODataListBinding;
				}
				if (!conf.filters) conf.filters = [];

				row[key] = (row[key] as string || "")
					.split(",")
					.map((id: string) => {
						if (id) {
							conf.filters.push(new Filter("id", FilterOperator.EQ, id));
						}
						return { id, name: id ? "loading..." : "" };
					});
			}
		});
	}

	private async fetchObjectsForComplexTypes(): Promise<void> {
		for (const key in Tree.COMPLEX_TYPES) {
			const conf = Tree.COMPLEX_TYPES[key];
			if (!conf.listBinding || !conf.filters?.length) continue;

			conf.objects = [];
			const batchSize = 100;
			while (conf.filters.length) {
				const batch = conf.filters.splice(0, batchSize);
				const filter = new Filter({ filters: batch, and: false });
				conf.listBinding.filter([filter]);
				const contexts = await conf.listBinding.requestContexts();
				const objs = await Promise.all(contexts.map((c) => c.requestObject()));
				conf.objects = conf.objects.concat(objs);
			}
			conf.objectMap = new Map(conf.objects.map((o) => [o.id, o]));
		}
	}

	private replaceIdsWithObjects(
		aRows: Record<string, unknown>[],
		mColumns: Record<string, { type: string }>
	): void {
		aRows.forEach((row) => {
			for (const key in row) {
				if (!mColumns[key]) continue;
				const type = mColumns[key].type;
				if (!Tree.isComplexType(type)) continue;

				const map = Tree.COMPLEX_TYPES[type].objectMap;
				if (!map) continue;

				row[key] = (row[key] as { id: string }[]).map((obj: { id: string }) => map.get(obj.id));
				if (Array.isArray(row[key])) {
					row[key + "_concat"] = (row[key] as { name: string }[])
						.map((obj) => (obj && obj.name ? obj.name : ""))
						.filter(Boolean)
						.join(", ");
				} else {
					row[key + "_concat"] = "";
				}
			}
		});
	}

	// ---------- Hierarchy and binding ----------

	private buildHierarchy(aRows: Record<string, unknown>[], groupColumn?: string): Array<{ [key : string] : unknown}> {
		const nodes = new Map(aRows.map((n) => [n.id, n]));
		const roots: Array<{ [key: string]: unknown}> = [];

		aRows.forEach((node) => {
			const parentId = node[groupColumn];
			if (!groupColumn || parentId === "0" || !nodes.has(parentId)) {
				roots.push(node as { [key : string] : string | Array<unknown>});
			} else {
				const parent = nodes.get(parentId);
				if (parent) {
					if (!parent.children) parent.children = [];
					(parent.children as Array<{ [key: string]: string | Array<unknown>}>).push(node as { [key: string]: string | Array<unknown> });
				}
			}
		});

		if (this.level) {
			return this.groupHighestLevel(roots, this.level[1]);
		}
		return roots;
	}

	private groupHighestLevel(aRows: Array<{[key : string] : unknown}>, sKey: string): Array<{ name: string; children: Array< unknown >; isGroupHeader: boolean }> {
		const groups: Array<{ name: string; children: unknown[]; isGroupHeader: boolean }> = [];
		for (const row of aRows) {
            if (typeof row[sKey] !== 'string') continue;
			let group = groups.find((g) => g.name === row[sKey]);
			if (!group) {
				group = { name: row[sKey], children: [], isGroupHeader: true };
				groups.push(group);
			}
			group.children.push(row);
		}
		return groups;
	}

	private bindRows(rootNodes: Array<{ [key: string] : unknown }>): void {
		if (!this.treeTable) return;

		const items = this.level ? rootNodes : rootNodes;
		const count = items.length;

		const rowModel = new JSONModel({
			items,
			currentRowCount: count,
			count,
			headers: this.columns,
		});
		this.treeTable.setModel(rowModel, "rowModel");

		this.treeTable.bindRows({
			path: "rowModel>/items/",
			parameters: { arrayNames: ["children"] },
		});
	}

	// ---------- Export / Copy ----------

	private getSortedHeadersForExport(): Array<{ [key : string]: { name: string; type: string; width: string; pos?: number } }> {
		const model = this.treeTable?.getModel("rowModel") as JSONModel;
        if (!model) return [];
        const headers = model.getProperty("/headers") as Record<string, { name: string; type: string; width: string; pos?: number }>;
        if (!headers) return [];
		const entries: Array<[string, { name: string; type: string; width: string; pos?: number }]> = Object.entries(headers);
		entries.sort((a, b) => {
			if (a[1].pos < b[1].pos) return -1;
			if (a[1].pos > b[1].pos) return 1;
			return 0;
		});
		// For non-date/validate types, use <col>_concat for export
		entries.forEach((h) => {
			const type = h[1].type;
			if (type !== "date" && type !== "" && type !== "validate") {
				h[0] = h[0] + "_concat";
			}
		});
		return entries.map(([field, meta]) => ({ [field]: meta }));
	}

	private getColumnHeaderMapForExport(): Record<string, string> {
		const sorted = this.getSortedHeadersForExport();
		const map: Record<string, string> = {};
		sorted.forEach((entry) => {
			const [field, meta] = Object.entries(entry)[0];
			map[field] = meta.name;
		});
		return map;
	}

	private getFlatItemsForExport(): Array<{ [key: string]: string | number | boolean | object }> {
		if (!this.treeTable) return [];
		const model = this.treeTable.getModel("rowModel") as JSONModel;
		const items = model.getProperty("/items") as Array<{ [key: string]: unknown }>;
		const out: Array<{ [key: string]: string | number | boolean | object }> = [];
		const walk = (nodes: Array<{ [key: string]: unknown }>) => {
			for (const n of nodes) {
				if (!n) continue;
				if (!n.isGroupHeader) {
                    out.push(n as { [key: string]: string | number | boolean | object });
                }
				if (Array.isArray(n.children) && n.children.length) walk(n.children as Array<{ [key: string]: unknown }>);
			}
		};
		walk(items);
		return out;
	}

	private onExport(): void {
		if (!this.treeTable) return;
		const data = this.getFlatItemsForExport();
		const columnHeaderMap = this.getColumnHeaderMapForExport();
		ExportUtils.export({
			data,
			columnHeaderMap,
			expand: [],
			toClipboard: false,
			filename: (this.title || "export") + ".xlsx",
		}).catch((e) => {
            console.error("Export failed:", e);
            
        });
	}

	private onCopy(): void {
		if (!this.treeTable) return;
		const data = this.getFlatItemsForExport();
		const columnHeaderMap = this.getColumnHeaderMapForExport();
		ExportUtils.export({
			data,
			columnHeaderMap,
			expand: [],
			toClipboard: true,
		}).catch((e) => {
            console.error("Copy to clipboard failed:", e);
        });

	}

	// ---------- Helpers ----------

	private getContainer(): VBox {
		const container = this.controller.getView().byId(this.subtaskId) as VBox;
		if (!container || typeof container.addItem !== "function") {
			throw new Error(`Subtask container ${this.subtaskId} not found or incompatible.`);
		}
		return container;
	}
}
